import React, { useRef, useState, useEffect } from 'react';
import { Download, Star, Trophy, MapPin, FileText, Hash, Eye, Scale, ArrowLeft, Ship, Package, Loader2, MessageSquare } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { SearchableSelect } from './ui/searchable-select';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { LOGO_URL } from '../constants/brand';
import { QUOTE_VIEW_CURRENCY_OPTIONS } from '../constants/currencies';
import { exchangeRatesApi } from '../services/api';
import { SCOPE_OPTIONS_BY_MODE, getChargeHeadsForModeAndScopes } from '../config/chargeHeadsByModeLeg';
import InquiryCreatorIdentity from './InquiryCreatorIdentity';

const DASH_PLACEHOLDER_VALUES = new Set(['-', '—', 'â€”']);

const isMeaningfulValue = (value) => {
  if (value == null) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return Boolean(trimmed) && !DASH_PLACEHOLDER_VALUES.has(trimmed);
  }
  return true;
};

const getDisplayValue = (value, fallback = '—') => (
  isMeaningfulValue(value) ? value : fallback
);

const formatBidderDisplay = (bid) => {
  const name = bid?.bidder_name || 'Member';
  const company = bid?.bidder_company;
  return company ? `${name} - ${company}` : name;
};

const normalizeSpecialContainers = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object');
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed || DASH_PLACEHOLDER_VALUES.has(trimmed)) return [];

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item === 'object') : [];
  } catch {
    return [];
  }
};

const BidComparison = ({
  quote,
  bids,
  onNominate,
  onMessageMember,
  onClose,
  currentMemberId,
  currentUserDisplayName = ''
}) => {
  const NOMINATION_REASON_OPTIONS = [
    'Got better deal',
    'Problem with vendor',
    'Better transit time',
    'Better service level',
    'Faster response time',
    'Better communication',
    'More reliable vendor',
    'Preferred partner request',
    'Customer changed requirement',
    'Pricing revised by another bidder',
    'Documentation/compliance issue',
    'Other'
  ];
  const comparisonRef = useRef(null);
  const [showNominateDialog, setShowNominateDialog] = useState(false);
  const [bidToNominate, setBidToNominate] = useState(null);
  const [nominationForm, setNominationForm] = useState({ rating: 5, reasonCategory: 'Got better deal', otherReason: '' });
  const [downloading, setDownloading] = useState(false);
  const [nominating, setNominating] = useState(false);
  const [showBidDetailDialog, setShowBidDetailDialog] = useState(false);
  const [bidToViewDetail, setBidToViewDetail] = useState(null);
  // Selection view: user selects members via checkboxes, then clicks Compare to see only comparison
  const [selectedBidIds, setSelectedBidIds] = useState([]);
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState({ USD: 1 });
  const [exchangeRateInfo, setExchangeRateInfo] = useState(null);
  const [exchangeRatesLoading, setExchangeRatesLoading] = useState(false);

  useEffect(() => {
    if (!showComparisonView && bids?.length) {
      setSelectedBidIds([]);
    }
  }, [bids?.length, showComparisonView]);

  useEffect(() => {
    let cancelled = false;
    setExchangeRatesLoading(true);
    exchangeRatesApi.getLatest()
      .then((response) => {
        if (cancelled) return;
        const rates = { USD: 1, ...(response.data?.rates || {}) };
        setExchangeRates(rates);
        setExchangeRateInfo(response.data || null);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to load exchange rates:', error);
          toast.error('Currency rates could not be loaded. Showing USD values.');
          setSelectedCurrency('USD');
          setExchangeRateInfo(null);
        }
      })
      .finally(() => {
        if (!cancelled) setExchangeRatesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const getModeIdFromQuoteType = (quoteType) => {
    switch (quoteType) {
      case 'Ocean FCL':
        return 'ocean-fcl';
      case 'Ocean LCL':
        return 'ocean-lcl';
      case 'Air':
        return 'air';
      case 'Transport':
        return 'transport';
      case 'Warehousing':
        return 'warehousing';
      case 'Breakbulk':
        return 'breakbulk';
      default:
        return null;
    }
  };

  const chargeCodeToName = (() => {
    const modeId = getModeIdFromQuoteType(quote?.quote_type);
    if (!modeId) return {};
    const scopes = SCOPE_OPTIONS_BY_MODE[modeId] || [];
    const heads = getChargeHeadsForModeAndScopes(modeId, scopes);
    return Object.fromEntries(heads.map((h) => [h.code, h.name]));
  })();

  // Calculate totals for a bid
  const calculateBidTotal = (bid) => {
    let subtotal = 0;
    let taxAmount = 0;
    let atActuals = 0;

    (bid.line_items || []).forEach(item => {
      if (item.is_at_actuals) {
        atActuals += (item.unit_price || 0) * (item.quantity || 1);
      } else {
        const lineTotal = (item.unit_price || 0) * (item.quantity || 1);
        subtotal += lineTotal;
        if (item.tax_applicable && item.tax_rate) {
          taxAmount += lineTotal * (item.tax_rate / 100);
        }
      }
    });

    return {
      subtotal,
      taxAmount,
      atActuals,
      total: subtotal + taxAmount + atActuals
    };
  };

  const getCurrencyRate = (currency = 'USD') => {
    if (!currency || currency === 'USD') return 1;
    const rate = Number(exchangeRates[currency]);
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  };

  const convertAmount = (amount, sourceCurrency = 'USD') => {
    const value = Number(amount || 0);
    const sourceRate = getCurrencyRate(sourceCurrency);
    const targetRate = getCurrencyRate(selectedCurrency);
    if (!Number.isFinite(value) || !sourceRate || !targetRate) {
      return value;
    }
    return (value / sourceRate) * targetRate;
  };

  const formatRateNumber = (rate) => (
    Number(rate || 0).toLocaleString(undefined, {
      maximumFractionDigits: 6,
      minimumFractionDigits: 0,
    })
  );

  const getExchangeRateUsedText = () => {
    const targetRate = getCurrencyRate(selectedCurrency);
    const rateDate = exchangeRateInfo?.updated_for_ist_date
      ? ` on ${exchangeRateInfo.updated_for_ist_date}`
      : '';
    if (!targetRate) return `Rate unavailable for ${selectedCurrency}`;
    if (selectedCurrency === 'USD') return `Base currency USD${rateDate}`;
    return `1 USD = ${formatRateNumber(targetRate)} ${selectedCurrency}${rateDate}`;
  };

  const getDownloadCurrencyRows = () => {
    const rows = [
      ['Display currency', selectedCurrency],
      ['Exchange rate used', getExchangeRateUsedText()],
    ];
    if (exchangeRateInfo?.time_last_update_utc) {
      rows.push(['Provider update UTC', exchangeRateInfo.time_last_update_utc]);
    }
    if (exchangeRateInfo?.fetched_at) {
      rows.push(['Fetched at UTC', exchangeRateInfo.fetched_at]);
    }
    if (exchangeRateInfo?.stale) {
      rows.push(['Rate status', 'Stale cached rate']);
    }
    return rows;
  };

  const addPdfCurrencyMetadata = (pdf, margin, yPos, pageWidth) => {
    pdf.setFontSize(8);
    pdf.setTextColor(90, 90, 90);
    const lines = [
      `Display currency: ${selectedCurrency}`,
      `Exchange rate used: ${getExchangeRateUsedText()}`,
    ];
    if (exchangeRateInfo?.time_last_update_utc) {
      lines.push(`Provider update UTC: ${exchangeRateInfo.time_last_update_utc}`);
    }
    if (exchangeRateInfo?.stale) {
      lines.push('Rate status: stale cached rate');
    }

    lines.forEach((line) => {
      const wrapped = pdf.splitTextToSize(line, pageWidth - (2 * margin));
      pdf.text(wrapped, margin, yPos);
      yPos += 4 * wrapped.length;
    });
    return yPos + 4;
  };

  const handleCurrencyChange = (value) => {
    const nextCurrency = value || 'USD';
    if (!getCurrencyRate(nextCurrency)) {
      toast.error(`Exchange rate for ${nextCurrency} is not available.`);
      return;
    }
    setSelectedCurrency(nextCurrency);
  };

  const formatMoney = (amount, sourceCurrency = 'USD') => {
    const converted = convertAmount(amount, sourceCurrency);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: selectedCurrency,
        maximumFractionDigits: 2,
      }).format(converted);
    } catch {
      return `${selectedCurrency} ${converted.toFixed(2)}`;
    }
  };

  const formatMoneyPlain = (amount, sourceCurrency = 'USD') => (
    `${selectedCurrency} ${convertAmount(amount, sourceCurrency).toFixed(2)}`
  );

  const getBidCurrency = (bid) => bid?.currency || 'USD';

  const getLineItemCurrency = (item, bid) => item?.currency || getBidCurrency(bid);

  const getLineItemContribution = (item) => {
    const lineTotal = (item.unit_price || 0) * (item.quantity || 1);
    const tax = !item.is_at_actuals && item.tax_applicable && item.tax_rate ? lineTotal * (item.tax_rate / 100) : 0;
    return lineTotal + tax;
  };

  const renderCurrencySelector = () => (
    <div className="min-w-[280px] max-w-full">
      <div className="flex items-center gap-2">
        <Label className="text-xs text-gray-500 whitespace-nowrap">Currency</Label>
        <SearchableSelect
          options={QUOTE_VIEW_CURRENCY_OPTIONS}
          value={selectedCurrency}
          onChange={handleCurrencyChange}
          placeholder="Search currency"
          disabled={exchangeRatesLoading}
          className="h-8 bg-white"
        />
      </div>
      <p className="mt-1 text-right text-[11px] leading-4 text-gray-500">
        {exchangeRatesLoading ? 'Loading exchange rates...' : getExchangeRateUsedText()}
      </p>
    </div>
  );

  // Sort bids by total (lowest first)
  const sortedBids = [...(bids || [])].sort((a, b) => {
    const totalA = convertAmount(calculateBidTotal(a).total, getBidCurrency(a));
    const totalB = convertAmount(calculateBidTotal(b).total, getBidCurrency(b));
    return totalA - totalB;
  });

  // Bids selected for comparison (when in comparison view)
  const comparisonBids = sortedBids.filter((b) => selectedBidIds.includes(b.bid_id));
  const currentNominatedBid = sortedBids.find((bid) => bid.bid_id === quote?.nominated_bid_id) || null;
  const nominationChangeCount = Number(quote?.nomination_change_count || 0);
  const remainingNominationChanges = Math.max(0, 3 - nominationChangeCount);
  const canShowNominationActions = (
    quote?.requester_id === currentMemberId &&
    ['Active', 'Nominated'].includes(quote?.status) &&
    !quote?.rfq_completed_at
  );
  // For PDF we use first 3 of comparison bids or all if fewer
  const bidsToCompare = comparisonBids.length > 0 ? comparisonBids.slice(0, 3) : sortedBids.slice(0, 3);
  const quoteFormData = quote?.form_data || {};
  const quoteType = quote?.quote_type || '';
  const isOceanFCL = quoteType === 'Ocean FCL';
  const isOceanLCL = quoteType === 'Ocean LCL';
  const isOcean = isOceanFCL || isOceanLCL;
  const scope = quoteFormData.scope || [];
  const scopeDisplayOptions = (() => {
    const modeId = getModeIdFromQuoteType(quoteType);
    return (modeId && SCOPE_OPTIONS_BY_MODE[modeId]) || [];
  })();
  const containersObj = quoteFormData.containers || quote.containers;
  const reefer = quoteFormData.reefer || quote.reefer || {};
  const oogList = normalizeSpecialContainers(quoteFormData.oogContainers || quote.oogContainers);

  // Quote details display helpers (same style as Submit Quotation / Quote Details)
  const getQuoteScopeDisplay = () => {
    const scope = quote.form_data?.scope || quote.scope;
    if (!scope || !Array.isArray(scope) || scope.length === 0) return '—';
    return scope.join(', ');
  };
  const STANDARD_CONTAINER_TYPES = ['20GP', '20HC', '40GP', '40HC', '45HC'];
  const getQuoteStandardContainerDisplay = () => {
    const containers = quote.containers || quote.form_data?.containers;
    if (!containers) return '—';
    const parts = [];
    if (Array.isArray(containers)) {
      STANDARD_CONTAINER_TYPES.forEach((type) => {
        const c = containers.find((x) => x?.container_type === type);
        const qty = c?.quantity ?? 0;
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    } else if (typeof containers === 'object') {
      STANDARD_CONTAINER_TYPES.forEach((type) => {
        const qty = containers[type] ?? 0;
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    }
    return parts.length > 0 ? parts.join(', ') : '—';
  };
  const getQuoteSpecialContainerDisplay = () => {
    const reefer = quote.reefer || quote.form_data?.reefer || {};
    const oog = quote.oogContainers || quote.form_data?.oogContainers || [];
    const parts = [];
    if (typeof reefer === 'object') {
      Object.entries(reefer).forEach(([type, qty]) => {
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    }
    if (Array.isArray(oog) && oog.length > 0) parts.push(`${oog.length} Special Containers`);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const formatPortDisplay = (name, code) => {
    const n = (name || '').trim();
    const c = (code || '').trim();
    if (!n && !c) return '';
    if (n && c && (n.endsWith(`(${c})`) || n === c)) return n;
    if (n && c) return `${n} (${c})`;
    return n || c;
  };

  // POL/POD display (same format as email: origin -> destination; use ASCII so PDF renders correctly)
  const getPolPodDisplay = () => {
    const origin = formatPortDisplay(quote.origin_port?.name, quote.origin_port?.code) || quote.origin_city || 'N/A';
    const dest = formatPortDisplay(quote.destination_port?.name, quote.destination_port?.code) || quote.destination_city || 'N/A';
    return `${origin} -> ${dest}`;
  };

  // Number and type of containers (combined std + special, same idea as email container_summary)
  const getQuoteContainerSummary = () => {
    const std = getQuoteStandardContainerDisplay();
    const special = getQuoteSpecialContainerDisplay();
    if (std === '—' && special === '—') return '—';
    if (special === '—') return std;
    if (std === '—') return special;
    return `${std}; ${special}`;
  };

  const getQuoteInsuranceDisplay = () => {
    const insuranceValue = quoteFormData?.insuranceValue ?? quoteFormData?.customsValue ?? quoteFormData?.customsDetails?.customsValue;
    const hasInsuranceValue = insuranceValue !== '' && insuranceValue != null;
    if (!quoteFormData?.insuranceRequired && !hasInsuranceValue) return '';
    if (!hasInsuranceValue) return 'Required';
    const numericValue = Number(insuranceValue);
    const displayValue = Number.isFinite(numericValue)
      ? formatMoney(numericValue, 'USD')
      : `${selectedCurrency} ${insuranceValue}`;
    return `${quoteFormData?.insuranceRequired ? 'Required - ' : ''}Invoice Value ${displayValue}`;
  };
  const quoteInsuranceDisplay = getQuoteInsuranceDisplay();

  const getBidDisplayName = (bid) => {
    if (!bid) return 'Unknown bidder';
    const name = formatBidderDisplay(bid);
    const company = bid.bidder_company && bid.bidder_company !== name ? bid.bidder_company : '';
    return company ? `${name} (${company})` : name;
  };

  // Handle nomination
  const handleNominate = async () => {
    const isChangingNomination = Boolean(currentNominatedBid);
    const trimmedOtherReason = nominationForm.otherReason.trim();
    if (isChangingNomination && nominationForm.reasonCategory === 'Other' && trimmedOtherReason.length < 3) {
      toast.error('Please enter the custom reason');
      return;
    }
    const finalReason = !isChangingNomination
      ? ''
      : nominationForm.reasonCategory === 'Other'
        ? trimmedOtherReason
        : nominationForm.reasonCategory;
    try {
      setNominating(true);
      await onNominate(bidToNominate.bid_id, {
        reason: finalReason,
        rating: nominationForm.rating,
        reasonCategory: isChangingNomination ? nominationForm.reasonCategory : '',
        otherReason: isChangingNomination && nominationForm.reasonCategory === 'Other' ? trimmedOtherReason : ''
      });
      setShowNominateDialog(false);
      setBidToNominate(null);
      setNominationForm({ rating: 5, reasonCategory: 'Got better deal', otherReason: '' });
    } finally {
      setNominating(false);
    }
  };

  // Load ONE logo from URL at runtime for PDF (returns base64 or null)
  const loadLogoAsBase64 = async () => {
    try {
      const res = await fetch(LOGO_URL, { mode: 'cors' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  // Generate PDF
  const downloadPDF = async (type = 'comparison') => {
    setDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Header: ONE logo or text
      const logoData = await loadLogoAsBase64();
      if (logoData) {
        const logoW = 36;
        const logoH = 14;
        pdf.addImage(logoData, 'PNG', margin, yPos, logoW, logoH);
        yPos += logoH + 4;
      } else {
        pdf.setFontSize(20);
        pdf.setTextColor(30, 58, 138);
        pdf.text('One Globe Alliance', margin, yPos);
        yPos += 8;
      }

      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(type === 'comparison' ? 'Bid Comparison Report' : 'Quote Summary', margin, yPos);
      yPos += 6;

      pdf.setFontSize(9);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPos);
      yPos += 5;
      yPos = addPdfCurrencyMetadata(pdf, margin, yPos, pageWidth);

      // Quote Details (full quotation details)
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, yPos, pageWidth - 2 * margin, 68, 'F');
      yPos += 6;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 30, 30);
      pdf.text('Quote Details', margin + 3, yPos);
      pdf.setFont('helvetica', 'normal');
      yPos += 6;

      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      const left = margin + 3;
      const wrap = (str, maxLen = 88) => (str.length > maxLen ? str.substring(0, maxLen) + '…' : str);
      pdf.text(wrap(`Quotation: ${quote.reference_number || 'N/A'}`), left, yPos);
      pdf.text(`Type: ${quote.quote_type || '—'}`, left + 98, yPos);
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('POL. POD', left, yPos);
      yPos += 4;
      pdf.text(wrap(getPolPodDisplay(), 90), left, yPos);
      yPos += 5;
      pdf.setFontSize(9);
      pdf.text(wrap(`Commodity: ${quote.commodity || quote.form_data?.commodity || 'N/A'}`), left, yPos);
      pdf.text(`Priority: ${quote.priority || 'Normal'}`, left + 98, yPos);
      yPos += 5;
      pdf.text(wrap(`Requested by: ${quote.requester_name || quote.requester_company || quote.requester_company_name || 'Member'}`), left, yPos);
      pdf.text(`Status: ${quote.status}`, left + 98, yPos);
      yPos += 5;
      pdf.text(`Incoterm: ${quote.incoterm || quote.form_data?.incoterm || '—'}`, left, yPos);
      pdf.text(`Bids Received: ${bids.length}`, left + 98, yPos);
      yPos += 5;
      if (quoteInsuranceDisplay) {
        pdf.text(wrap(`Insurance: ${quoteInsuranceDisplay}`), left, yPos);
        yPos += 5;
      }
      pdf.text(wrap(`Scope: ${getQuoteScopeDisplay()}`), left, yPos);
      yPos += 5;
      pdf.text(wrap(`Number and type of containers: ${getQuoteContainerSummary()}`), left, yPos);
      yPos += 10;

      if (type === 'comparison' && bidsToCompare.length > 0) {
        // Comparison Table Header
        pdf.setFontSize(11);
        pdf.setTextColor(30, 30, 30);
        pdf.text('Bid Comparison', margin, yPos);
        yPos += 8;

        // Table header
        const colWidth = (pageWidth - 2 * margin - 60) / bidsToCompare.length;
        pdf.setFillColor(59, 130, 246);
        pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');

        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text('Charge', margin + 2, yPos);
        pdf.text('Container', margin + 35, yPos);

        const bidderLabel = (bid) => {
          const name = formatBidderDisplay(bid);
          return name.length > 14 ? name.substring(0, 12) + '…' : name;
        };
        bidsToCompare.forEach((bid, idx) => {
          const xPos = margin + 60 + (idx * colWidth);
          pdf.text(bidderLabel(bid), xPos, yPos);
        });
        yPos += 8;

        // Collect all unique charges with container types (use charge_code for Charge column)
        const allCharges = new Map();
        bidsToCompare.forEach(bid => {
          (bid.line_items || []).forEach(item => {
            const key = `${item.charge_code || item.description || 'Misc'}-${item.container_type || 'All'}`;
            if (!allCharges.has(key)) {
              allCharges.set(key, {
                chargeCode: item.charge_code || item.description || 'Misc',
                container_type: item.container_type || 'All',
                values: {}
              });
            }
            allCharges.get(key).values[bid.bid_id] = item;
          });
        });

        // Table rows
        pdf.setTextColor(50, 50, 50);
        let rowIndex = 0;
        allCharges.forEach(charge => {
          if (yPos > pageHeight - 30) {
            pdf.addPage();
            yPos = margin;
          }

          if (rowIndex % 2 === 0) {
            pdf.setFillColor(249, 250, 251);
            pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 6, 'F');
          }

          pdf.setFontSize(7);
          const chargeDisplayName = chargeCodeToName[charge.chargeCode] || charge.chargeCode || '';
          pdf.text((chargeDisplayName || '').substring(0, 18), margin + 2, yPos);
          pdf.text(charge.container_type, margin + 35, yPos);

          bidsToCompare.forEach((bid, idx) => {
            const xPos = margin + 60 + (idx * colWidth);
            const item = charge.values[bid.bid_id];
            if (item) {
              const contribution = getLineItemContribution(item);
              const amount = item.is_at_actuals
                ? `${formatMoneyPlain(contribution, getLineItemCurrency(item, bid))} (at actual)`
                : formatMoneyPlain(contribution, getLineItemCurrency(item, bid));
              pdf.text(amount, xPos, yPos);
            } else {
              pdf.text('-', xPos, yPos);
            }
          });
          yPos += 6;
          rowIndex++;
        });

        // Totals row
        yPos += 2;
        pdf.setFillColor(229, 231, 235);
        pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');

        pdf.setFontSize(9);
        pdf.setTextColor(30, 30, 30);
        pdf.text('TOTAL', margin + 2, yPos);

        const colWidth2 = (pageWidth - 2 * margin - 60) / bidsToCompare.length;
        bidsToCompare.forEach((bid, idx) => {
          const xPos = margin + 60 + (idx * colWidth2);
          const totals = calculateBidTotal(bid);
          pdf.text(formatMoneyPlain(totals.total, getBidCurrency(bid)), xPos, yPos);
        });
        yPos += 12;

        // Bidder (company) details
        pdf.setFontSize(11);
        pdf.text('Bidder Information', margin, yPos);
        yPos += 8;

        bidsToCompare.forEach((bid) => {
          if (yPos > pageHeight - 40) {
            pdf.addPage();
            yPos = margin;
          }

          const memberName = formatBidderDisplay(bid);
          pdf.setFontSize(9);
          pdf.setTextColor(59, 130, 246);
          pdf.text(memberName, margin, yPos);
          yPos += 5;

          pdf.setTextColor(80, 80, 80);
          pdf.text(`Validity: ${bid.validity_days || 'N/A'} days`, margin + 5, yPos);
          pdf.text(`Submitted: ${new Date(bid.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}`, margin + 60, yPos);
          yPos += 5;

          if (bid.remarks) {
            pdf.text(`Remarks: ${bid.remarks.substring(0, 80)}`, margin + 5, yPos);
            yPos += 5;
          }
          yPos += 3;
        });
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('© 2026 One Globe Alliance - Members Portal', margin, pageHeight - 10);
      pdf.text(`Page 1 of 1`, pageWidth - margin - 20, pageHeight - 10);

      // Save
      const quoteLabel = (quote.reference_number || quote.quote_id).replace(/[^a-zA-Z0-9-_]/g, '_');
      const filename = type === 'comparison'
        ? `Quote_${quoteLabel}_Comparison.pdf`
        : `Quote_${quoteLabel}_Summary.pdf`;
      pdf.save(filename);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Download a single bid's quotation as PDF in comparison format (one column)
  const downloadSingleBidPDF = async (bid) => {
    setDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      const logoData = await loadLogoAsBase64();
      if (logoData) {
        pdf.addImage(logoData, 'PNG', margin, yPos, 36, 14);
        yPos += 18;
      } else {
        pdf.setFontSize(20);
        pdf.setTextColor(30, 58, 138);
        pdf.text('One Globe Alliance', margin, yPos);
        yPos += 8;
      }
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Quotation (Comparison format)', margin, yPos);
      yPos += 6;
      pdf.setFontSize(9);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPos);
      yPos += 5;
      yPos = addPdfCurrencyMetadata(pdf, margin, yPos, pageWidth);

      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, yPos, pageWidth - 2 * margin, 68, 'F');
      yPos += 6;
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 30, 30);
      pdf.text('Quote Details', margin + 3, yPos);
      pdf.setFont('helvetica', 'normal');
      yPos += 6;
      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      const left = margin + 3;
      const wrap = (str, maxLen = 88) => (str.length > maxLen ? str.substring(0, maxLen) + '…' : str);
      pdf.text(wrap(`Quotation: ${quote.reference_number || 'N/A'}`), left, yPos);
      pdf.text(`Type: ${quote.quote_type || '—'}`, left + 98, yPos);
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('POL. POD', left, yPos);
      yPos += 4;
      pdf.text(wrap(getPolPodDisplay(), 90), left, yPos);
      yPos += 5;
      pdf.setFontSize(9);
      pdf.text(wrap(`Commodity: ${quote.commodity || quote.form_data?.commodity || 'N/A'}`), left, yPos);
      pdf.text(`Priority: ${quote.priority || 'Normal'}`, left + 98, yPos);
      yPos += 5;
      pdf.text(wrap(`Requested by: ${quote.requester_name || quote.requester_company || quote.requester_company_name || 'Member'}`), left, yPos);
      pdf.text(`Status: ${quote.status}`, left + 98, yPos);
      yPos += 5;
      pdf.text(`Incoterm: ${quote.incoterm || quote.form_data?.incoterm || '—'}`, left, yPos);
      yPos += 5;
      pdf.text(wrap(`Scope: ${getQuoteScopeDisplay()}`), left, yPos);
      yPos += 5;
      pdf.text(wrap(`Number and type of containers: ${getQuoteContainerSummary()}`), left, yPos);
      yPos += 10;

      pdf.setFontSize(11);
      pdf.setTextColor(30, 30, 30);
      pdf.text('Bid Comparison', margin, yPos);
      yPos += 8;
      pdf.setFillColor(59, 130, 246);
      pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Charge', margin + 2, yPos);
      pdf.text('Container', margin + 35, yPos);
      const memberLabel = formatBidderDisplay(bid).substring(0, 30);
      pdf.text(memberLabel, margin + 60, yPos);
      yPos += 8;

      pdf.setTextColor(50, 50, 50);
      (bid.line_items || []).forEach((item, rowIndex) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = margin;
        }
        if (rowIndex % 2 === 0) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 6, 'F');
        }
        pdf.setFontSize(7);
        const chargeCode = (item.charge_code || item.description || 'Misc').substring(0, 18);
        pdf.text(chargeCode, margin + 2, yPos);
        pdf.text(item.container_type || 'All', margin + 35, yPos);
        const contribution = getLineItemContribution(item);
        const amount = item.is_at_actuals
          ? `${formatMoneyPlain(contribution, getLineItemCurrency(item, bid))} (at actual)`
          : formatMoneyPlain(contribution, getLineItemCurrency(item, bid));
        pdf.text(amount, margin + 60, yPos);
        yPos += 6;
      });

      yPos += 2;
      pdf.setFillColor(229, 231, 235);
      pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
      pdf.setFontSize(9);
      pdf.setTextColor(30, 30, 30);
      pdf.text('TOTAL', margin + 2, yPos);
      pdf.text(formatMoneyPlain(calculateBidTotal(bid).total, getBidCurrency(bid)), margin + 60, yPos);
      yPos += 12;

      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('© 2026 One Globe Alliance - Members Portal', margin, pageHeight - 10);

      const quoteLabel = (quote.reference_number || quote.quote_id).replace(/[^a-zA-Z0-9-_]/g, '_');
      const memberSlug = formatBidderDisplay(bid).replace(/[^a-zA-Z0-9-_]/g, '_');
      pdf.save(`Quote_${quoteLabel}_${memberSlug}_Comparison.pdf`);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Download comparison as CSV/Excel
  const downloadComparisonCSV = () => {
    if (!comparisonBids?.length) return;
    try {
      const headers = ['Charge', 'Container', ...comparisonBids.map((bid) => formatBidderDisplay(bid))];
      const allCharges = new Map();
      comparisonBids.forEach((bid) => {
        (bid.line_items || []).forEach((item) => {
          const key = `${item.charge_code || item.description || 'Misc'}-${item.container_type || 'All'}`;
          if (!allCharges.has(key)) {
            allCharges.set(key, {
              chargeCode: item.charge_code || item.description || 'Misc',
              container_type: item.container_type || 'All',
              values: {}
            });
          }
          allCharges.get(key).values[bid.bid_id] = item;
        });
      });
      const chargeRows = Array.from(allCharges.values());
      const rows = chargeRows.map((charge) => {
        const bidValues = comparisonBids.map((bid) => {
          const item = charge.values[bid.bid_id];
          if (!item) return '';
          const contribution = getLineItemContribution(item);
          return item.is_at_actuals
            ? `${formatMoneyPlain(contribution, getLineItemCurrency(item, bid))} (at actual)`
            : formatMoneyPlain(contribution, getLineItemCurrency(item, bid));
        });
        return [
          `"${(chargeCodeToName[charge.chargeCode] || charge.chargeCode).replace(/"/g, '""')}"`,
          `"${charge.container_type.replace(/"/g, '""')}"`,
          ...bidValues.map((v) => `"${v.replace(/"/g, '""')}"`)
        ];
      });
      // Totals row
      const totalRow = ['TOTAL', '', ...comparisonBids.map((bid) => formatMoneyPlain(calculateBidTotal(bid).total, getBidCurrency(bid)))];
      // Metadata rows
      const meta = [
        ['Quotation', quote.reference_number || 'N/A'],
        ['Type', quote.quote_type || '—'],
        ['Commodity', quote.commodity || quote.form_data?.commodity || 'N/A'],
        ['Requested by', quote.requester_name || quote.requester_company || quote.requester_company_name || 'Member'],
        ['Status', quote.status],
        ...getDownloadCurrencyRows(),
        ['Scope', getQuoteScopeDisplay()],
        [''],
      ];
      const csvContent = [
        ...meta.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
        headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','),
        ...rows.map((r) => r.join(',')),
        totalRow.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const quoteLabel = (quote.reference_number || quote.quote_id).replace(/[^a-zA-Z0-9-_]/g, '_');
      link.setAttribute('download', `Quote_${quoteLabel}_Comparison.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Comparison CSV downloaded!');
    } catch (error) {
      console.error('CSV download error:', error);
      toast.error('Failed to download CSV');
    }
  };

  // Download full report: one page per bidder (comparison format)
  const downloadDetailedPDF = async () => {
    if (!comparisonBids?.length) return;
    setDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const wrap = (str, maxLen = 88) => (str.length > maxLen ? str.substring(0, maxLen) + '…' : str);
      const left = margin + 3;

      const logoData = await loadLogoAsBase64();

      comparisonBids.forEach((bid, bidIndex) => {
        if (bidIndex > 0) pdf.addPage();
        let yPos = margin;

        if (logoData) {
          pdf.addImage(logoData, 'PNG', margin, yPos, 36, 14);
          yPos += 18;
        } else {
          pdf.setFontSize(20);
          pdf.setTextColor(30, 58, 138);
          pdf.text('One Globe Alliance', margin, yPos);
          yPos += 8;
        }
        pdf.setFontSize(12);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Full Report – One bidder per page', margin, yPos);
        yPos += 6;
        pdf.setFontSize(9);
        pdf.text(`Page ${bidIndex + 1} of ${comparisonBids.length} – ${formatBidderDisplay(bid).substring(0, 30)}`, margin, yPos);
        yPos += 5;
        yPos = addPdfCurrencyMetadata(pdf, margin, yPos, pageWidth);

        pdf.setFillColor(245, 247, 250);
        pdf.rect(margin, yPos, pageWidth - 2 * margin, 68, 'F');
        yPos += 6;
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 30, 30);
        pdf.text('Quote Details', left, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 6;
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        pdf.text(wrap(`Quotation: ${quote.reference_number || 'N/A'}`), left, yPos);
        pdf.text(`Type: ${quote.quote_type || '—'}`, left + 98, yPos);
        yPos += 5;
        pdf.setFontSize(8);
        pdf.text('POL. POD', left, yPos);
        yPos += 4;
        pdf.text(wrap(getPolPodDisplay(), 90), left, yPos);
        yPos += 5;
        pdf.setFontSize(9);
        pdf.text(wrap(`Commodity: ${quote.commodity || quote.form_data?.commodity || 'N/A'}`), left, yPos);
        pdf.text(`Priority: ${quote.priority || 'Normal'}`, left + 98, yPos);
        yPos += 5;
        pdf.text(wrap(`Requested by: ${quote.requester_name || quote.requester_company || quote.requester_company_name || 'Member'}`), left, yPos);
        pdf.text(`Status: ${quote.status}`, left + 98, yPos);
        yPos += 5;
        pdf.text(`Incoterm: ${quote.incoterm || quote.form_data?.incoterm || '—'}`, left, yPos);
        yPos += 5;
        pdf.text(wrap(`Scope: ${getQuoteScopeDisplay()}`), left, yPos);
        yPos += 5;
        pdf.text(wrap(`Number and type of containers: ${getQuoteContainerSummary()}`), left, yPos);
        yPos += 10;

        pdf.setFontSize(11);
        pdf.setTextColor(30, 30, 30);
        pdf.text('Bid Comparison', margin, yPos);
        yPos += 8;
        pdf.setFillColor(59, 130, 246);
        pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text('Charge', margin + 2, yPos);
        pdf.text('Container', margin + 35, yPos);
        const memberLabel = formatBidderDisplay(bid).substring(0, 30);
        pdf.text(memberLabel, margin + 60, yPos);
        yPos += 8;

        pdf.setTextColor(50, 50, 50);
        (bid.line_items || []).forEach((item, rowIndex) => {
          if (yPos > pageHeight - 30) {
            pdf.addPage();
            yPos = margin;
          }
          if (rowIndex % 2 === 0) {
            pdf.setFillColor(249, 250, 251);
            pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 6, 'F');
          }
          pdf.setFontSize(7);
          const rawCharge = item.charge_code || item.description || 'Misc';
          const chargeDisplayName = chargeCodeToName[rawCharge] || rawCharge;
          pdf.text((chargeDisplayName || '').substring(0, 18), margin + 2, yPos);
          pdf.text(item.container_type || 'All', margin + 35, yPos);
          const contribution = getLineItemContribution(item);
          const amount = item.is_at_actuals
            ? `${formatMoneyPlain(contribution, getLineItemCurrency(item, bid))} (at actual)`
            : formatMoneyPlain(contribution, getLineItemCurrency(item, bid));
          pdf.text(amount, margin + 60, yPos);
          yPos += 6;
        });

        yPos += 2;
        pdf.setFillColor(229, 231, 235);
        pdf.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
        pdf.setFontSize(9);
        pdf.setTextColor(30, 30, 30);
        pdf.text('TOTAL', margin + 2, yPos);
        pdf.text(formatMoneyPlain(calculateBidTotal(bid).total, getBidCurrency(bid)), margin + 60, yPos);
        yPos += 12;

        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`© 2026 One Globe Alliance - Members Portal  |  Page ${bidIndex + 1} of ${comparisonBids.length}`, margin, pageHeight - 10);
      });

      const quoteLabel = (quote.reference_number || quote.quote_id).replace(/[^a-zA-Z0-9-_]/g, '_');
      pdf.save(`Quote_${quoteLabel}_FullReport.pdf`);
      toast.success('Full report downloaded!');
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Failed to generate full report');
    } finally {
      setDownloading(false);
    }
  };

  // ---------- Comparison view only: charge table + Back ----------
  if (showComparisonView && comparisonBids.length >= 2) {
    const allCharges = new Map();
    comparisonBids.forEach((bid) => {
      (bid.line_items || []).forEach((item) => {
        const key = `${item.charge_code || item.description || 'Misc'}-${item.container_type || 'All'}`;
        if (!allCharges.has(key)) {
          allCharges.set(key, {
            chargeCode: item.charge_code || item.description || 'Misc',
            container_type: item.container_type || 'All',
            values: {}
          });
        }
        allCharges.get(key).values[bid.bid_id] = item;
      });
    });
    const chargeRows = Array.from(allCharges.values());

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowComparisonView(false)}>
            <ArrowLeft size={14} className="mr-1" />
            Change selection
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            {renderCurrencySelector()}
            <Button variant="outline" size="sm" onClick={() => downloadPDF('summary')} disabled={downloading}>
              <FileText size={14} className="mr-1" />
              Quote PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadPDF('comparison')} disabled={downloading || comparisonBids.length === 0}>
              <Download size={14} className="mr-1" />
              Comparison PDF
            </Button>
            <Button variant="outline" size="sm" onClick={downloadComparisonCSV} disabled={comparisonBids.length === 0}>
              <Download size={14} className="mr-1" />
              Download Excel
            </Button>
            <Button size="sm" onClick={downloadDetailedPDF} disabled={downloading || comparisonBids.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Download size={14} className="mr-1" />
              {downloading ? 'Generating...' : 'Download Full Report'}
            </Button>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Bid Comparison ({selectedCurrency})</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Charge</th>
                  <th className="px-4 py-3 text-left font-semibold">Container</th>
                  {comparisonBids.map((bid) => (
                    <th key={bid.bid_id} className="px-4 py-3 text-left font-semibold">
                      {formatBidderDisplay(bid)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chargeRows.map((charge, rowIdx) => (
                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-gray-900">{chargeCodeToName[charge.chargeCode] || charge.chargeCode}</td>
                    <td className="px-4 py-2 text-gray-600">{charge.container_type}</td>
                    {comparisonBids.map((bid) => {
                      const item = charge.values[bid.bid_id];
                      if (!item) return <td key={bid.bid_id} className="px-4 py-2 text-gray-400">—</td>;
                      const contribution = getLineItemContribution(item);
                      const amount = item.is_at_actuals
                        ? `${formatMoney(contribution, getLineItemCurrency(item, bid))} (at actual)`
                        : formatMoney(contribution, getLineItemCurrency(item, bid));
                      return <td key={bid.bid_id} className="px-4 py-2 text-gray-900">{amount}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-200 font-semibold text-gray-900">
                  <td className="px-4 py-3" colSpan={2}>TOTAL</td>
                  {comparisonBids.map((bid) => (
                    <td key={bid.bid_id} className="px-4 py-3">{formatMoney(calculateBidTotal(bid).total, getBidCurrency(bid))}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Selection view: checkboxes + Compare button ----------
  const toggleBidSelection = (bidId) => {
    setSelectedBidIds((prev) =>
      prev.includes(bidId) ? prev.filter((id) => id !== bidId) : [...prev, bidId]
    );
  };
  const selectCount = selectedBidIds.length;
  const canCompare = selectCount >= 2;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Bid Comparison</h2>
          <p className="text-sm text-gray-500">
            Select at least 2 members to compare, then click Compare.
          </p>
        </div>
        {renderCurrencySelector()}
      </div>

      {/* Quotation details */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={quote.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
              {quote.status}
            </Badge>
            {quote.priority && (
              <Badge variant="secondary">{quote.priority} Priority</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 text-xs block">Requested by</span>
              <InquiryCreatorIdentity
                name={quote.requester_id === currentMemberId && currentUserDisplayName
                  ? currentUserDisplayName
                  : (quote.requester_name || quote.requester_company || quote.requester_company_name)}
                profilePicture={quote.requester_profile_picture}
                companyName={quote.requester_company || quote.requester_company_name}
                label={null}
                compact
                className="mt-1"
              />
            </div>
            <div>
              <span className="text-gray-500 text-xs flex items-center gap-1">
                <Hash size={12} /> Quotation
              </span>
              <p className="font-mono font-medium">{quote.reference_number || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Quote Type</span>
              <p className="font-medium">{quote.quote_type || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs flex items-center gap-1">
                <MapPin size={12} /> Route
              </span>
              <p className="font-medium">
                {quote.origin_port?.name || quote.origin_city || 'N/A'} → {quote.destination_port?.name || quote.destination_city || 'N/A'}
              </p>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Commodity</span>
              <p className="font-medium">{quote.commodity || quote.form_data?.commodity || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Incoterm</span>
              <p className="font-medium">{quote.incoterm || quote.form_data?.incoterm || '—'}</p>
            </div>
            {quoteInsuranceDisplay && (
              <div>
                <span className="text-gray-500 text-xs block">Insurance</span>
                <p className="font-medium">{quoteInsuranceDisplay}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500 text-xs block">Scope</span>
              <p className="font-medium break-words">{getQuoteScopeDisplay()}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Containers (Std)</span>
              <p className="font-medium">{getQuoteStandardContainerDisplay()}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs block">Containers (Special Containers)</span>
              <p className="font-medium">{getQuoteSpecialContainerDisplay()}</p>
            </div>
          </div>

          {isOcean && (scope.length > 0 || scopeDisplayOptions.length > 0) && (
            <div className="p-4 bg-white/70 rounded-lg">
              <h4 className="font-semibold mb-3">Scope (Select all that apply)</h4>
              <div className="flex flex-wrap gap-2">
                {scopeDisplayOptions.length > 0 ? scopeDisplayOptions.map((opt) => (
                  <span
                    key={opt}
                    className={`inline-flex items-center px-3 py-1 rounded text-sm ${scope.includes(opt) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}
                  >
                    ○ {opt}
                  </span>
                )) : (
                  <span className="text-sm text-gray-500">â€”</span>
                )}
              </div>
            </div>
          )}

          {isOcean && (
            <div className="p-4 bg-white/70 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center">
                <MapPin size={16} className="mr-2 text-gray-500" />
                Port Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {(quoteFormData.por || quote.por) && (
                  <div>
                    <span className="text-gray-500 text-xs block">POR (Place of Receipt)</span>
                    <p className="font-medium">{formatPortDisplay(quoteFormData.porName, quoteFormData.por || quote.por)}</p>
                  </div>
                )}
                {(quoteFormData.fpod || quote.fpod) && (
                  <div>
                    <span className="text-gray-500 text-xs block">FPOD (Final Place of Delivery)</span>
                    <p className="font-medium">{formatPortDisplay(quoteFormData.fpodName, quoteFormData.fpod || quote.fpod)}</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-500 text-xs block">POL (Port of Loading)</span>
                  <p className="font-medium">{formatPortDisplay(quote.origin_port?.name, quote.origin_port?.code) || quote.origin_city || 'â€”'}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">POD (Port of Discharge)</span>
                  <p className="font-medium">{formatPortDisplay(quote.destination_port?.name, quote.destination_port?.code) || quote.destination_city || 'â€”'}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">Incoterm</span>
                  <p className="font-medium">{quote.incoterm || quoteFormData.incoterm || 'â€”'}</p>
                </div>
              </div>
            </div>
          )}

          {isOceanFCL && (
            <div className="p-4 bg-white/70 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center">
                <Ship size={16} className="mr-2 text-gray-500" />
                Standard Containers
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {STANDARD_CONTAINER_TYPES.map((type) => {
                  const containerEntry = Array.isArray(containersObj)
                    ? containersObj.find((item) => item?.container_type === type)
                    : null;
                  const quantity = Array.isArray(containersObj)
                    ? Number(containerEntry?.quantity || 0)
                    : Number(containersObj?.[type] || 0);
                  return (
                    <div key={type}>
                      <span className="text-gray-500 text-xs block">{type}</span>
                      <p className="font-semibold text-2xl leading-none mt-1">{quantity || 0}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isOceanFCL && (
            <div className="p-4 bg-white/70 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center">
                <Package size={16} className="mr-2 text-gray-500" />
                Special Containers
              </h4>
              {(Object.values(reefer).some((qty) => Number(qty) > 0) || oogList.length > 0) ? (
                <div className="space-y-4 text-sm">
                  {Object.entries(reefer).some(([, qty]) => Number(qty) > 0) && (
                    <div>
                      <span className="text-gray-500 text-xs block mb-2">Reefer Containers</span>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(reefer)
                          .filter(([, qty]) => Number(qty) > 0)
                          .map(([type, qty]) => (
                            <span key={type} className="inline-flex items-center px-3 py-1 rounded bg-blue-100 text-blue-800">
                              {qty}x {type}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                  {oogList.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-gray-500 text-xs block">Out of Gauge / Special Containers</span>
                      {oogList.map((container, idx) => (
                        <div key={container.id || idx} className="p-3 bg-white rounded border">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            <div>
                              <span className="text-gray-500 text-xs block">Type</span>
                              <p className="font-medium">{getDisplayValue(container.type)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500 text-xs block">Quantity</span>
                              <p className="font-medium">{container.quantity || 0}</p>
                            </div>
                            <div>
                              <span className="text-gray-500 text-xs block">Weight / Container</span>
                              <p className="font-medium">{getDisplayValue(container.weightPerContainer || container.weight)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500 text-xs block">Dimensions</span>
                              <p className="font-medium">
                                {(() => {
                                  const dims = [container.length, container.width, container.height].filter(isMeaningfulValue);
                                  if (!dims.length) return '—';
                                  const unit = getDisplayValue(container.unit, 'cm');
                                  return `${dims.join(' × ')} ${unit}`;
                                })()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">—</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Member list with checkboxes — no comparison until Compare is clicked */}
      <div ref={comparisonRef} className="bg-white p-4 rounded-lg border">
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 w-12">Select</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Member</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Total ({selectedCurrency})</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Valid</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 max-w-[200px]">Remarks</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700 w-[280px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedBids.map((bid, index) => {
                const totals = calculateBidTotal(bid);
                const isNominated = quote?.nominated_bid_id === bid.bid_id;
                const isHighlighted = currentNominatedBid ? isNominated : index === 0;
                const checked = selectedBidIds.includes(bid.bid_id);
                const canMessageBidder =
                  typeof onMessageMember === 'function' &&
                  quote?.requester_id === currentMemberId &&
                  Boolean(bid.bidder_id) &&
                  !['Cancelled', 'Completed', 'Expired'].includes(quote?.status) &&
                  (!quote?.nominated_member_id || quote.nominated_member_id === bid.bidder_id);
                const messageUnavailableReason = !bid.bidder_id
                  ? 'The member details for this submitted quote are unavailable'
                  : quote?.requester_id !== currentMemberId
                    ? 'Only the inquiry creator can message a quoted member'
                    : ['Cancelled', 'Completed', 'Expired'].includes(quote?.status)
                      ? 'Messaging is unavailable for this inquiry'
                      : quote?.nominated_member_id && quote.nominated_member_id !== bid.bidder_id
                        ? 'This inquiry has been nominated to another member'
                        : '';
                return (
                  <tr key={bid.bid_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleBidSelection(bid.bid_id)}
                      />
                    </td>
                    <td className="px-4 py-2 font-medium text-gray-900">
                      <span className="inline-flex items-center gap-1">
                        {isHighlighted && <Trophy size={14} className="text-yellow-500 shrink-0" />}
                        {formatBidderDisplay(bid)}
                      </span>
                    </td>
                    <td className={`px-4 py-2 text-right font-semibold ${isHighlighted ? 'text-green-600' : 'text-gray-900'}`}>
                      {formatMoney(totals.total, getBidCurrency(bid))}
                    </td>
                    <td className="px-4 py-2 text-center text-gray-600">{bid.validity_days ?? 'N/A'} days</td>
                    <td className="px-4 py-2 text-gray-600">{new Date(bid.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="px-4 py-2 text-gray-600 max-w-[200px] truncate" title={bid.remarks || ''}>
                      {bid.remarks || '—'}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0"
                          onClick={() => { setBidToViewDetail(bid); setShowBidDetailDialog(true); }}
                          title="View quotation details"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0"
                          onClick={() => downloadSingleBidPDF(bid)}
                          disabled={downloading}
                          title="Download quote as PDF (comparison format)"
                        >
                          <Download size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => onMessageMember?.(bid)}
                          disabled={!canMessageBidder}
                          title={messageUnavailableReason || `Message ${formatBidderDisplay(bid)}`}
                          data-testid={`message-member-${bid.bid_id}`}
                        >
                          <MessageSquare size={14} className="mr-1" />
                          Message Member
                        </Button>
                        {canShowNominationActions && (
                          <Button
                            size="sm"
                            onClick={() => { setBidToNominate(bid); setShowNominateDialog(true); }}
                            className={
                              quote.status === 'Nominated' && quote.nominated_bid_id === bid.bid_id
                                ? 'bg-[#F59E0B] hover:bg-[#D97706] text-white border-[#F59E0B]'
                                : 'bg-purple-600 hover:bg-purple-700 text-white'
                            }
                            disabled={nominating || (quote.status === 'Nominated' && quote.nominated_bid_id === bid.bid_id) || (quote.status === 'Nominated' && remainingNominationChanges <= 0)}
                          >
                            <Trophy size={14} className="mr-1 text-white" />
                            {quote.status === 'Nominated' && quote.nominated_bid_id === bid.bid_id ? 'Nominated' : 'Nominate'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <p className="text-sm text-gray-500">
            {selectCount} selected. Select at least 2 to compare.
          </p>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            disabled={!canCompare}
            onClick={() => setShowComparisonView(true)}
          >
            <Scale size={14} className="mr-1" />
            Compare
          </Button>
        </div>
      </div>

      {/* Bid quotation details dialog (Eye icon) */}
      <Dialog open={showBidDetailDialog} onOpenChange={(open) => { setShowBidDetailDialog(open); if (!open) setBidToViewDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={20} />
              Quotation details
            </DialogTitle>
          </DialogHeader>
          {bidToViewDetail && (() => {
            const totals = calculateBidTotal(bidToViewDetail);
            const bidderLabel = formatBidderDisplay(bidToViewDetail);
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">Member</span>
                  <span className="font-medium">{bidderLabel}</span>
                  <span className="text-gray-500">Valid (days)</span>
                  <span>{bidToViewDetail.validity_days ?? '—'}</span>
                  <span className="text-gray-500">Remarks</span>
                  <span className="min-w-0 whitespace-pre-wrap break-words">{bidToViewDetail.remarks || '—'}</span>
                  <span className="text-gray-500">Currency</span>
                  <span>{selectedCurrency}</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Line items</h4>
                  <div className="border rounded overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="px-3 py-2 text-left">Charge / Description</th>
                          <th className="px-3 py-2 text-left">Container</th>
                          <th className="px-3 py-2 text-right">Unit</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Unit price ({selectedCurrency})</th>
                          <th className="px-3 py-2 text-center">At actual</th>
                          <th className="px-3 py-2 text-right">Tax</th>
                          <th className="px-3 py-2 text-right">Line total ({selectedCurrency})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(bidToViewDetail.line_items || []).map((item, idx) => {
                          const lineTotal = (item.unit_price || 0) * (item.quantity || 1);
                          const tax = item.tax_applicable && item.tax_rate ? lineTotal * (item.tax_rate / 100) : 0;
                          const itemCurrency = getLineItemCurrency(item, bidToViewDetail);
                          return (
                            <tr key={idx} className="border-b last:border-b-0">
                              <td className="px-3 py-2">{item.charge_code || item.description || '—'}</td>
                              <td className="px-3 py-2">{item.container_type || '—'}</td>
                              <td className="px-3 py-2 text-right">{item.unit || '—'}</td>
                              <td className="px-3 py-2 text-right">{item.quantity ?? '—'}</td>
                              <td className="px-3 py-2 text-right">{formatMoney(item.unit_price || 0, itemCurrency)}</td>
                              <td className="px-3 py-2 text-center">{item.is_at_actuals ? 'Yes' : '—'}</td>
                              <td className="px-3 py-2 text-right">{item.tax_applicable && item.tax_rate ? `${item.tax_type || ''} ${item.tax_rate}%` : '—'}</td>
                              <td className="px-3 py-2 text-right">{formatMoney(lineTotal + tax, itemCurrency)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex justify-end gap-6 text-sm border-t pt-3">
                  <span>Subtotal: {formatMoney(totals.subtotal, getBidCurrency(bidToViewDetail))}</span>
                  {totals.taxAmount > 0 && <span>Tax: {formatMoney(totals.taxAmount, getBidCurrency(bidToViewDetail))}</span>}
                  {totals.atActuals > 0 && <span>At actuals: {formatMoney(totals.atActuals, getBidCurrency(bidToViewDetail))}</span>}
                  <span className="font-semibold">Total: {formatMoney(totals.total, getBidCurrency(bidToViewDetail))}</span>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Nomination Dialog */}
      <Dialog open={showNominateDialog} onOpenChange={setShowNominateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{currentNominatedBid ? 'Change Nominated Bid' : 'Nominate Winning Bid'}</DialogTitle>
          </DialogHeader>
          {bidToNominate && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm">
                  <strong>Bidder:</strong> {getBidDisplayName(bidToNominate)}
                </p>
                <p className="text-sm">
                  <strong>Total:</strong> {formatMoney(calculateBidTotal(bidToNominate).total, getBidCurrency(bidToNominate))}
                </p>
                {currentNominatedBid && currentNominatedBid.bid_id !== bidToNominate.bid_id && (
                  <p className="text-sm mt-2 text-gray-700">
                    You are about to change the nominated person from {getBidDisplayName(currentNominatedBid)} to {getBidDisplayName(bidToNominate)}.
                  </p>
                )}
                {quote?.status === 'Nominated' && !quote?.rfq_completed_at && (
                  <p className="text-xs mt-2 text-gray-600">
                    Changes used: {nominationChangeCount}/3. Remaining: {remainingNominationChanges}.
                  </p>
                )}
              </div>

              {currentNominatedBid && !quote?.rfq_completed_at && (
                <div>
                  <Label>Reason for Change *</Label>
                  <select
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={nominationForm.reasonCategory}
                    onChange={(e) => setNominationForm({
                      ...nominationForm,
                      reasonCategory: e.target.value,
                      otherReason: e.target.value === 'Other' ? nominationForm.otherReason : ''
                    })}
                  >
                    {NOMINATION_REASON_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              )}

              {currentNominatedBid && !quote?.rfq_completed_at && nominationForm.reasonCategory === 'Other' && (
                <div>
                  <Label>Other Reason *</Label>
                  <Textarea
                    value={nominationForm.otherReason}
                    onChange={(e) => setNominationForm({ ...nominationForm, otherReason: e.target.value })}
                    placeholder="Type the custom reason..."
                    rows={2}
                  />
                </div>
              )}

              <div>
                <Label>Rating for {getBidDisplayName(bidToNominate)} *</Label>
                <div className="flex items-center space-x-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNominationForm({ ...nominationForm, rating: star })}
                      className="focus:outline-none"
                    >
                      <Star
                        size={28}
                        className={star <= nominationForm.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                      />
                    </button>
                  ))}
                  <span className="text-sm text-gray-600 ml-2">{nominationForm.rating}/5</span>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowNominateDialog(false)}>
                  Cancel
                </Button>
                {canShowNominationActions && (
                  <Button
                    onClick={handleNominate}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={nominating || (quote?.status === 'Nominated' && currentNominatedBid?.bid_id !== bidToNominate.bid_id && remainingNominationChanges <= 0)}
                  >
                    {nominating ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : currentNominatedBid ? 'Confirm Change' : 'Confirm Nomination'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BidComparison;
